import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * The widget's card: one glass surface, one radius, one title — so every card on a campaign
 * page (How it works, Completed, the leaderboard, Info sections, …) is the same object.
 *
 * - **Radius** is always the outer `--border-radius`. `--border-radius-inner` is for things
 *   nested INSIDE a card (task rows, a prize board in a section), never for a card itself.
 * - **`title`** renders the one card heading (an `<h3>` in the section-label voice, foreground
 *   colour) with an optional `aside` at the far end of the title row (a min-bet pill, a
 *   time-left chip). Give every card its heading through this prop, not with an ad-hoc label.
 * - **`inset`** is the content padding: `"default"` (16px all round, title row 12px above the
 *   body) or `"none"` for content that runs edge to edge — tables, rows with dividers, cover
 *   art — where the title row keeps its own inset and the body manages its own.
 * - **`urgent`** lights the rim in the urgent colour; **`active`** in the progress colour;
 *   **`primary`** in the brand colour (an invitation to act: the Opt In card); **`won`** casts
 *   the prize colour from the top centre into the rim and fill.
 */
export type CardProps = HTMLAttributes<HTMLElement> & {
  title?: ReactNode;
  aside?: ReactNode;
  inset?: "default" | "none";
  urgent?: boolean;
  /** Lights the rim and a small top glow in the progress colour (`--active`): the rung or state
   *  the player is in right now. */
  active?: boolean;
  /** Lights the rim and a top glow in the brand colour (`--primary`): a card that invites an
   *  action (the desktop Opt In card). */
  primary?: boolean;
  /** A prize the player has won: gold light cast from the top centre (where a step badge
   *  sits) into the rim and fill, fading out towards the sides and bottom. */
  won?: boolean;
  /** Extra classes on the title row (rarely needed). */
  titleRowClassName?: string;
};

export const Card = forwardRef<HTMLElement, CardProps>(
  (
    {
      title,
      aside,
      inset = "default",
      urgent = false,
      active = false,
      primary = false,
      won = false,
      titleRowClassName,
      className,
      children,
      ...props
    },
    ref
  ) => (
    <section
      ref={ref}
      className={cn(
        // `--card-pad` (12px phones, 16px desktop) is the card's inset; edge-to-edge bodies
        // (inset="none") use it too, so their rows line up with the title.
        "glass-card w-full overflow-hidden rounded-(--border-radius) [--card-pad:12px] lg:[--card-pad:16px]",
        urgent && "glass-card-urgent",
        active && "glass-card-active",
        primary && "glass-card-primary",
        won && "glass-card-won",
        inset === "default" && "p-(--card-pad)",
        className
      )}
      {...props}
    >
      {title != null && (
        <CardTitle
          aside={aside}
          className={cn(
            inset === "default"
              ? "mb-2 lg:mb-3"
              : "px-(--card-pad) pt-(--card-pad) pb-2 lg:pb-3",
            titleRowClassName
          )}
        >
          {title}
        </CardTitle>
      )}
      {children}
    </section>
  )
);
Card.displayName = "Card";

/**
 * The card heading row on its own, for a card that places its title somewhere the `title` prop
 * can't reach (e.g. after a cover). Same voice as `SectionLabel`, in the foreground colour.
 */
export const CardTitle = ({
  aside,
  className,
  children,
}: {
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("flex items-center justify-between gap-3", className)}>
    <h3 className="min-w-0 flex-1 font-heading text-[10px] leading-[13px] font-semibold tracking-[0.8px] text-foreground uppercase lg:text-[11px] lg:leading-[14px] lg:tracking-[0.88px]">
      {children}
    </h3>
    {/* The aside (a 24px time chip, a min-bet pill) overhangs the row instead of growing it:
        the row stays as tall as the title, so every card's body starts the same distance under
        its heading whether or not something sits beside it. */}
    {aside != null && (
      <div className="-my-1 flex shrink-0 items-center">{aside}</div>
    )}
  </div>
);
